import React, { useState, useEffect } from 'react';
import {
  Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Loader2,
  MessageSquare, Building, User, Calendar, Check, X, Eye,
  ChevronRight, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get admin key from localStorage (set during login)
const getAdminKey = () => localStorage.getItem('admin_key') || '';

export const AdminPendingChangesPage = () => {
  const [changes, setChanges] = useState({ pending: [], approved: [], rejected: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedChange, setSelectedChange] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadChanges();
  }, [filter]);

  const loadChanges = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pending-changes/all?status=${filter}`, {
        headers: { 'X-Admin-Key': getAdminKey() }
      });
      if (res.ok) {
        const data = await res.json();
        setChanges(data);
      }
    } catch (err) {
      console.error('Failed to load changes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (changeId) => {
    setActionLoading(changeId);
    try {
      const res = await fetch(`${API_URL}/api/pending-changes/${changeId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY
        },
        body: JSON.stringify({ notes: notes || 'Approved' })
      });
      if (res.ok) {
        setSelectedChange(null);
        setNotes('');
        loadChanges();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to approve');
      }
    } catch (err) {
      alert('Failed to approve change');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (changeId) => {
    if (!notes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setActionLoading(changeId);
    try {
      const res = await fetch(`${API_URL}/api/pending-changes/${changeId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY
        },
        body: JSON.stringify({ notes })
      });
      if (res.ok) {
        setSelectedChange(null);
        setNotes('');
        loadChanges();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to reject');
      }
    } catch (err) {
      alert('Failed to reject change');
    } finally {
      setActionLoading(null);
    }
  };

  const getChangeTypeIcon = (type) => {
    const icons = {
      'greeting_message': MessageSquare,
      'business_hours': Clock,
      'profile': Building
    };
    return icons[type] || AlertCircle;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      'approved': { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const formatValue = (value) => {
    if (!value) return '—';
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object') {
        return JSON.stringify(parsed, null, 2);
      }
      return value;
    } catch {
      return value;
    }
  };

  const pendingList = filter === 'all' ? changes.pending : 
                     filter === 'pending' ? changes.pending :
                     filter === 'approved' ? changes.approved :
                     changes.rejected;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pending Changes</h1>
          <p className="text-gray-500">Review and approve customer setting changes</p>
        </div>
        <button
          onClick={loadChanges}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Pending Review"
          value={changes.pending_count || changes.pending?.length || 0}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          label="Approved"
          value={changes.approved?.length || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          label="Rejected"
          value={changes.rejected?.length || 0}
          icon={XCircle}
          color="red"
        />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4 mb-6">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 shadow-sm"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Changes List */}
        <div className="col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-800">Changes ({pendingList?.length || 0})</h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : pendingList?.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No {filter} changes</p>
              </div>
            ) : (
              pendingList?.map(change => {
                const Icon = getChangeTypeIcon(change.change_type);
                return (
                  <div
                    key={change.id}
                    onClick={() => setSelectedChange(change)}
                    className={`p-4 cursor-pointer hover:bg-blue-50 transition-colors ${
                      selectedChange?.id === change.id ? 'bg-blue-50 border-l-4 border-l-[#4F80F5]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#4F80F5]" />
                        <span className="font-medium text-gray-800">{change.client_name}</span>
                      </div>
                      {getStatusBadge(change.status)}
                    </div>
                    <div className="text-sm text-gray-500 capitalize">
                      {change.change_type?.replace('_', ' ')}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(change.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Detail View */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {selectedChange ? (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{selectedChange.client_name}</h2>
                  <p className="text-sm text-gray-500 capitalize">
                    {selectedChange.change_type?.replace('_', ' ')} • {selectedChange.field_name}
                  </p>
                </div>
                {getStatusBadge(selectedChange.status)}
              </div>

              {/* Content */}
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                {/* Change Details */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-semibold text-gray-800 mb-3">Change Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Submitted</span>
                      <span className="text-sm text-gray-800">
                        {new Date(selectedChange.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Change Type</span>
                      <span className="text-sm text-gray-800 capitalize">
                        {selectedChange.change_type?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Old Value */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <X className="w-4 h-4" />
                    Current Value
                  </h3>
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-red-100 overflow-x-auto">
                    {formatValue(selectedChange.old_value) || 'Not set'}
                  </pre>
                </div>

                {/* New Value */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Requested Value
                  </h3>
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-green-100 overflow-x-auto">
                    {formatValue(selectedChange.new_value)}
                  </pre>
                </div>

                {/* Review Info (if already reviewed) */}
                {selectedChange.status !== 'pending' && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3">Review Info</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Reviewed By</span>
                        <span className="text-sm text-gray-800">{selectedChange.reviewed_by || '—'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Reviewed At</span>
                        <span className="text-sm text-gray-800">
                          {selectedChange.reviewed_at 
                            ? new Date(selectedChange.reviewed_at).toLocaleString() 
                            : '—'}
                        </span>
                      </div>
                      {selectedChange.admin_notes && (
                        <div className="col-span-2">
                          <span className="text-xs text-gray-500 block mb-1">Notes</span>
                          <span className="text-sm text-gray-800">{selectedChange.admin_notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Area (only for pending) */}
                {selectedChange.status === 'pending' && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-3">Review Action</h3>
                    <div className="mb-4">
                      <label className="text-sm text-gray-600 block mb-2">
                        Notes (required for rejection)
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add notes about your decision..."
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 resize-none focus:ring-2 focus:ring-[#4F80F5] focus:border-transparent"
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleApprove(selectedChange.id)}
                        disabled={actionLoading === selectedChange.id}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium shadow-sm"
                      >
                        {actionLoading === selectedChange.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Approve & Apply
                      </button>
                      <button
                        onClick={() => handleReject(selectedChange.id)}
                        disabled={actionLoading === selectedChange.id}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium shadow-sm"
                      >
                        {actionLoading === selectedChange.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 min-h-[400px]">
              <div className="text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a change to review</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    yellow: { bg: 'bg-yellow-100', icon: 'text-yellow-600' },
    green: { bg: 'bg-green-100', icon: 'text-green-600' },
    red: { bg: 'bg-red-100', icon: 'text-red-600' }
  };
  const colorStyle = colors[color];

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{label}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorStyle.bg}`}>
          <Icon className={`w-5 h-5 ${colorStyle.icon}`} />
        </div>
      </div>
    </div>
  );
};

export default AdminPendingChangesPage;
